if (!customElements.get('fp-home-header')) {
  customElements.define(
    'fp-home-header',
    class extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-fp-home-menu-toggle]');
        this.menu = this.querySelector('[data-fp-home-menu]');
        this.overlay = this.querySelector('[data-fp-home-menu-overlay]');
        this.closeButton = this.querySelector('[data-fp-home-menu-close]');
        this.drawerBreakpoint = window.matchMedia('(max-width: 1139px)');

        if (!this.toggle || !this.menu || !this.overlay || !this.closeButton) return;

        this.openMenu = () => {
          if (!this.drawerBreakpoint.matches) return;
          this.toggle.setAttribute('aria-expanded', 'true');
          this.toggle.setAttribute('aria-label', 'Close menu');
          this.menu.setAttribute('aria-hidden', 'false');
          this.menu.removeAttribute('inert');
          this.overlay.setAttribute('aria-hidden', 'false');
          this.overlay.removeAttribute('tabindex');
          document.body.classList.add('fp-home-menu-open');
          requestAnimationFrame(() => this.closeButton.focus({ preventScroll: true }));
        };

        this.closeMenu = (restoreFocus = false) => {
          this.toggle.setAttribute('aria-expanded', 'false');
          this.toggle.setAttribute('aria-label', 'Open menu');
          this.menu.setAttribute('aria-hidden', 'true');
          this.menu.setAttribute('inert', '');
          this.overlay.setAttribute('aria-hidden', 'true');
          this.overlay.setAttribute('tabindex', '-1');
          document.body.classList.remove('fp-home-menu-open');
          if (restoreFocus) this.toggle.focus({ preventScroll: true });
        };

        this.toggleHandler = () => {
          const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
          if (isOpen) this.closeMenu(true);
          else this.openMenu();
        };

        this.menuClickHandler = (event) => {
          if (!event.target.closest('a')) return;
          this.closeMenu();
        };

        this.keydownHandler = (event) => {
          if (this.menu.getAttribute('aria-hidden') !== 'false') return;

          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeMenu(true);
            return;
          }

          if (event.key !== 'Tab') return;
          const focusable = Array.from(this.menu.querySelectorAll('a[href], button:not([disabled])'));
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        };

        this.breakpointHandler = () => {
          if (!this.drawerBreakpoint.matches) this.closeMenu();
        };

        this.overlayClickHandler = () => this.closeMenu(true);
        this.closeClickHandler = () => this.closeMenu(true);
        this.toggle.addEventListener('click', this.toggleHandler);
        this.overlay.addEventListener('click', this.overlayClickHandler);
        this.closeButton.addEventListener('click', this.closeClickHandler);
        this.menu.addEventListener('click', this.menuClickHandler);
        document.addEventListener('keydown', this.keydownHandler);
        this.drawerBreakpoint.addEventListener?.('change', this.breakpointHandler);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.toggleHandler);
        this.overlay?.removeEventListener('click', this.overlayClickHandler);
        this.closeButton?.removeEventListener('click', this.closeClickHandler);
        this.menu?.removeEventListener('click', this.menuClickHandler);
        document.removeEventListener('keydown', this.keydownHandler);
        this.drawerBreakpoint?.removeEventListener?.('change', this.breakpointHandler);
        document.body.classList.remove('fp-home-menu-open');
      }
    }
  );
}

if (!customElements.get('fp-home-marquee')) {
  customElements.define(
    'fp-home-marquee',
    class extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-fp-home-marquee-viewport]');
        this.track = this.querySelector('[data-fp-home-marquee-track]');
        this.base = this.querySelector('[data-fp-home-marquee-base]');
        this.clone = this.querySelector('[data-fp-home-marquee-clone]');
        this.secondsPerItem = Number(this.dataset.secondsPerItem) || 6;
        this.autoplay = this.dataset.autoplay !== 'false';
        this.pauseOnHover = this.dataset.pauseOnHover === 'true';
        this.hoverPaused = false;
        this.originalMarkup = this.base?.innerHTML || '';
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.inView = true;

        if (!this.viewport || !this.track || !this.base || !this.clone || !this.base.children.length) return;

        this.rebuild();
        this.resizeObserver = new ResizeObserver(() => this.scheduleRebuild());
        this.resizeObserver.observe(this.viewport);
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.inView = Boolean(entry?.isIntersecting || entry?.intersectionRatio > 0);
            this.updatePlayState();
          },
          { threshold: 0.01, rootMargin: '200px 0px' }
        );
        this.intersectionObserver.observe(this.viewport);
        this.visibilityHandler = () => this.updatePlayState();
        document.addEventListener('visibilitychange', this.visibilityHandler);
        this.motionHandler = () => {
          this.updatePlayState();
          if (!this.motionQuery.matches) this.rebuild();
        };
        this.motionQuery.addEventListener?.('change', this.motionHandler);
        this.pointerEnterHandler = () => {
          this.hoverPaused = true;
          this.updatePlayState();
        };
        this.pointerLeaveHandler = () => {
          this.hoverPaused = false;
          this.updatePlayState();
        };
        if (this.pauseOnHover) {
          this.viewport.addEventListener('pointerenter', this.pointerEnterHandler);
          this.viewport.addEventListener('pointerleave', this.pointerLeaveHandler);
        }
      }

      disconnectedCallback() {
        clearTimeout(this.resizeTimer);
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.motionQuery?.removeEventListener?.('change', this.motionHandler);
        this.viewport?.removeEventListener('pointerenter', this.pointerEnterHandler);
        this.viewport?.removeEventListener('pointerleave', this.pointerLeaveHandler);
      }

      scheduleRebuild() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.rebuild(), 120);
      }

      setInactive(nodes) {
        Array.from(nodes).forEach((node) => {
          node.setAttribute('aria-hidden', 'true');
          node.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          node.querySelectorAll('img, video').forEach((media) => media.setAttribute('aria-hidden', 'true'));
        });
      }

      rebuild() {
        this.track.classList.remove('is-active');
        this.track.style.animation = 'none';
        this.track.style.transform = 'translate3d(0, 0, 0)';
        this.base.innerHTML = this.originalMarkup;
        this.clone.innerHTML = '';

        const firstItem = this.base.firstElementChild;
        if (!firstItem) return;
        const gap = Number.parseFloat(getComputedStyle(this.base).columnGap || getComputedStyle(this.base).gap) || 0;
        const step = firstItem.getBoundingClientRect().width + gap;
        const originalCount = this.base.children.length;
        const originalItems = Array.from(this.base.children).map((item) => item.outerHTML);
        let safety = 0;

        while (
          (this.base.children.length < 12 || this.base.scrollWidth < this.viewport.clientWidth + step) &&
          safety < 40
        ) {
          this.base.insertAdjacentHTML('beforeend', originalItems[safety % originalItems.length]);
          safety += 1;
        }

        this.setInactive(Array.from(this.base.children).slice(originalCount));
        this.clone.innerHTML = this.base.innerHTML;
        this.setInactive(this.clone.children);
        const distance = this.base.getBoundingClientRect().width;
        const duration = distance / Math.max(step / this.secondsPerItem, 1);
        this.track.style.setProperty('--fp-home-feedback-distance', `${distance.toFixed(3)}px`);
        this.track.style.setProperty('--fp-home-feedback-duration', `${duration.toFixed(3)}s`);
        this.track.offsetWidth;
        this.track.style.animation = '';
        this.updatePlayState();
      }

      updatePlayState() {
        const running =
          this.autoplay && !this.motionQuery.matches && !document.hidden && this.inView && !this.hoverPaused;
        this.track.style.animationPlayState = running ? 'running' : 'paused';
        this.track.classList.toggle('is-active', running);
      }
    }
  );
}

if (!customElements.get('fp-before-after-card')) {
  customElements.define(
    'fp-before-after-card',
    class extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('[data-fp-before-after-range]');
        if (!this.range) return;

        const update = () => this.style.setProperty('--fp-before-position', `${this.range.value}%`);
        this.range.addEventListener('input', update);
        this.range.addEventListener('change', update);
        update();
      }
    }
  );
}
